import { Component, EventEmitter, OnInit } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { LeagueService } from '../services/league.service';
import { League, LeagueList } from '../models/league';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { LeagueListQueryFilter } from './league-list-query-filter';
import { provideNativeDateAdapter } from '@angular/material/core';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { CreateLeagueModalComponent } from '../modals/league/create-league-modal/create-league-modal.component';
import { EditLeagueModalComponent } from '../modals/league/edit-league-modal/edit-league-modal.component';
import { LeagueEventEmitters } from '../booking-extensions/services/league-event-emitters';


@Component({
    selector: 'league-list',
    templateUrl: './league-list.component.html',
    styleUrl: './league-list.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false,
    animations: [
        trigger('slideIn', [
            state('void', style({
                transform: 'translateX(100%)',
                opacity: 0
            })),
            state('*', style({
                transform: 'translateX(0)',
                opacity: 1
            })),
            transition('void => *', [
                animate('300ms ease-out')
            ]),
            transition('* => void', [
                animate('250ms ease-in')
            ])
        ])
    ]
})
export class LeagueListComponent extends BaseComponent implements OnInit {
    public leagues: LeagueList[] = [];
    public filter: LeagueListQueryFilter = new LeagueListQueryFilter();
    public updateFilterEvent = new EventEmitter<any>();
    public displayedColumns: string[] = ['name', 'description', 'iterationCount', 'memberCount', 'actions'];
    public selectedLeague: League | null = null;

    constructor(
        sessionService: SessionService,
        stringHelpers: StringHelpers,
        private dialog: MatDialog,
        private leagueService: LeagueService,
        private leagueEventEmitters: LeagueEventEmitters
    ) {
        super(sessionService, stringHelpers);
    }

    ngOnInit(): void {
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.getLeagues();
    }

    onViewIterations(league: League): void {
        const leagueData: League = {
            id: league.id,
            name: league.name,
            description: league.description,
            organizationId: league.organizationId,
            lastEditedBy: league.lastEditedBy
        };
        this.selectedLeague = leagueData;
        this.leagueEventEmitters.leagueChangedEvent.emit(leagueData);
    }

    onCloseIterationsPanel(): void {
        this.selectedLeague = null;
    }

    onIterationsUpdated(): void {
        this.getLeagues();
    }

    getLeagues(): void {
        this.isLoading = true;
        this.leagueService.getAllByOrganization(this.filter).subscribe({
            next: (result: any) => {
                this.leagues = result.results;
                this.filter.totalCount = result.totalCount;
                this.updateFilterEvent.emit(result.results);
                this.isLoading = false;
            },
            error: (error: any) => {
                this.isLoading = false;
                console.error(error);
                this.alert(error);
            }
        });
    }

    sortData(sort: Sort): void {
        const data = this.leagues.slice();
        if (!sort.active || sort.direction === '') {
            this.leagues = data;
            return;
        }

        this.leagues = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'name':
                    return this.compare(a.name, b.name, isAsc);
                case 'description':
                    return this.compare(a.description, b.description, isAsc);
                case 'iterationCount':
                    return this.compare(a.iterationCount, b.iterationCount, isAsc);
                case 'memberCount':
                    return this.compare(a.memberCount, b.memberCount, isAsc);
                default:
                    return 0;
            }
        });
    }

    compare(a: number | string, b: number | string, isAsc: boolean): number {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    resetFilter(): void {
        this.filter = new LeagueListQueryFilter();
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.filter.pageSize = 10;
    }

    onCreate(): void {
        const dialogRef = this.dialog.open(CreateLeagueModalComponent, {
            maxHeight: '90vh',
            maxWidth: '60vw',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getLeagues();
            }
        });
    }

    onEdit(league: League): void {
        const dialogRef = this.dialog.open(EditLeagueModalComponent, {
            width: '50vw',
            height: 'auto',
            disableClose: true,
        });

        dialogRef.componentInstance.league = league;

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getLeagues();
            }
        });
    }

    onDelete(league: League): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Delete League';
        dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> 
        Are you sure you want to delete league <strong>${league.name}</strong>? 
        <br /><br />All iterations and member associations will also be deleted.`;

        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.isLoading = true;
                this.leagueService.delete({
                    id: league.id,
                    lastEditedBy: this.sessionService.getUserId(),
                    organizationId: this.sessionService.getUser().organizationId,
                }).subscribe({
                    next: (result) => {
                        this.getLeagues();
                        this.isLoading = false;
                        if (this.selectedLeague && this.selectedLeague.id === league.id) {
                            this.selectedLeague = null;
                        }
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    alert(error: any): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

        if (error.status === 400) {
            error.error.forEach((error: any) => {
                errorMessage += `<li>${error.errorMessage}</li>`;
            });
        } else {
            errorMessage += `<li>There was an error loading the leagues, Please try again or contact support if the problem persists</li>`;
        }

        dialogRef.componentInstance.title = 'League Error';
        dialogRef.componentInstance.body = errorMessage + '</ul>';
        dialogRef.componentInstance.submitText = 'OK';
    }
}

